import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { ApiResponse } from '../_model/api-response';
import { AccountPinSecurity, ChangeAccountPinRequest, ChangePasswordRequest, ChangePasswordResponseData, ConfirmPinRequest, ConfirmPinResponseData, LoginData, LoginSuccessData, MiniUserDto, RefreshData, RegisterRequest, RegisterResponseData, ResetAccountPinRequest, SetupAccountPinRequest, TotpConfirmRequest, TotpSetupData, UpdateAccountPinSettingsRequest, UserDto } from '../_model/users';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private base = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}
  login(payload: {
    email: string;
    password: string;
    deviceFingerprint?: string | null;
    deviceName?: string | null;
  }): Observable<ApiResponse<LoginData>> {
    return this.http.post<ApiResponse<LoginData>>(`${this.base}/Auth/login`, payload);
  }

  loginWithPin(payload: {
    email: string;
    pin: string;
    deviceFingerprint?: string | null;
    deviceName?: string | null;
  }): Observable<ApiResponse<LoginData>> {
    return this.http.post<ApiResponse<LoginData>>(`${this.base}/Auth/login/pin`, payload);
  }

  refresh(payload: { refreshToken: string }): Observable<ApiResponse<RefreshData>> {
    return this.http.post<ApiResponse<RefreshData>>(`${this.base}/Auth/refresh`, payload);
  }
  register(payload: RegisterRequest): Observable<ApiResponse<RegisterResponseData>> {
    return this.http.post<ApiResponse<RegisterResponseData>>(
      `${this.base}/Auth/register`,
      payload
    );
  }
  me(): Observable<ApiResponse<MiniUserDto>> {
    return this.http.get<ApiResponse<MiniUserDto>>(
      `${this.base}/Auth/me`
    );
  }

  logout(refreshToken: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.base}/Auth/logout`,
      { refreshToken }
    );
  }
  confirmPin(payload: ConfirmPinRequest): Observable<ApiResponse<ConfirmPinResponseData>> {
    return this.http.post<ApiResponse<ConfirmPinResponseData>>(
      `${this.base}/Auth/confirm-pin`,
      payload
    );
  }
  changePassword(payload: ChangePasswordRequest): Observable<ApiResponse<ChangePasswordResponseData>> {
    return this.http.post<ApiResponse<ChangePasswordResponseData>>(
      `${this.base}/Auth/change-password`,
      payload
    );
  }


  getAccountPinSettings(): Observable<ApiResponse<AccountPinSecurity>> {
    return this.http.get<ApiResponse<AccountPinSecurity>>(`${this.base}/Auth/account-pin/settings`);
  }

  setupAccountPin(payload: SetupAccountPinRequest): Observable<ApiResponse<AccountPinSecurity>> {
    return this.http.post<ApiResponse<AccountPinSecurity>>(`${this.base}/Auth/account-pin/setup`, payload);
  }

  changeAccountPin(payload: ChangeAccountPinRequest): Observable<ApiResponse<AccountPinSecurity>> {
    return this.http.post<ApiResponse<AccountPinSecurity>>(`${this.base}/Auth/account-pin/change`, payload);
  }

  updateAccountPinSettings(payload: UpdateAccountPinSettingsRequest): Observable<ApiResponse<AccountPinSecurity>> {
    return this.http.put<ApiResponse<AccountPinSecurity>>(`${this.base}/Auth/account-pin/settings`, payload);
  }

  resetAccountPin(payload: ResetAccountPinRequest): Observable<ApiResponse<AccountPinSecurity>> {
    return this.http.post<ApiResponse<AccountPinSecurity>>(`${this.base}/Auth/account-pin/reset`, payload);
  }

  disableAccountPin(pin: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/Auth/account-pin/disable`, { pin });
  }

  forgotPassword(email: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/Auth/password/forgot`, { email });
  }

  validatePasswordResetToken(token: string): Observable<ApiResponse<{ valid: boolean; expiresAt?: string | null }>> {
    return this.http.post<ApiResponse<{ valid: boolean; expiresAt?: string | null }>>(
      `${this.base}/Auth/password/reset/validate`,
      { token }
    );
  }

  resetPassword(token: string, newPassword: string, confirmPassword: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.base}/Auth/password/reset`, {
      token,
      newPassword,
      confirmPassword
    });
  }
  resendPin(payload: { token: string }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      `${this.base}/Auth/resend-pin`,
      payload
    );
  }

  totpSetup(): Observable<ApiResponse<TotpSetupData>> {
    return this.http.post<ApiResponse<TotpSetupData>>(
      `${this.base}/Auth/otp/setup`,
      {}
    );
  }

  totpConfirm(payload: TotpConfirmRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      `${this.base}/Auth/otp/confirm`,
      payload
    );
  }
  
  loginTotp(payload: { challengeToken: string; code: string }): Observable<ApiResponse<LoginSuccessData>> {
    return this.http.post<ApiResponse<LoginSuccessData>>(
      `${this.base}/Auth/login/otp`,
      payload
    );
  }

  totpDisable(payload: { code: string }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.base}/Auth/otp/disable`, payload);
  }

  recoveryGenerate(): Observable<ApiResponse<string[]>> {
    return this.http.post<ApiResponse<string[]>>(`${this.base}/Auth/recovery-codes/generate`, {});
  }

  recoveryRegenerate(): Observable<ApiResponse<string[]>> {
    return this.http.post<ApiResponse<string[]>>(`${this.base}/Auth/recovery-codes/regenerate`, {});
  }

  recoveryStatus(): Observable<ApiResponse<{ remaining: number }>> {
    return this.http.get<ApiResponse<{ remaining: number }>>(`${this.base}/Auth/recovery-codes/status`);
  }
  
  loginRecovery(payload: { challengeToken: string; code: string }): Observable<ApiResponse<LoginSuccessData>> {
    return this.http.post<ApiResponse<LoginSuccessData>>(
      `${this.base}/Auth/login/recovery`,
      payload
    );
  }
  
  resetOtp(payload: { userId: string }): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(
      `${this.base}/Auth/otp/reset`,
      payload
    );
  }
  updateOtpReminder(nextReminder: string | null) {
    return this.http.patch<ApiResponse<string>>(
      `${this.base}/Auth/otp/reminder`,
      { nextReminder }
    );
  }
}
